<template>
  <div>
    <template v-if="generalStore.getFirebaseIntegration">
      <SharedSocialAuth @success="successAuth" />
      <div class="divider !text-white my-8">
        {{ $t("or") }}
      </div>
    </template>
    <form @submit.prevent="onSubmit">
      <div class="flex flex-wrap gap-2">
        <div class="max-lg:w-full lg:flex-grow">
          <UiInput
            v-model="form.first_name"
            class="flex-1"
            name="first_name"
            :placeholder="$t('firstName')"
            color="white"
          />
        </div>
        <div class="max-lg:w-full lg:flex-grow">
          <UiInput
            v-model="form.last_name"
            class="flex-1"
            name="last_name"
            :placeholder="$t('lastName')"
            color="white"
          />
        </div>
        <div class="w-full">
          <UiInput
            v-model="form.email"
            class="flex-1"
            name="email"
            :placeholder="$t('enterYourEmail')"
            color="white"
          />
        </div>
        <div class="w-full">
          <UiInput
            v-model="form.password"
            class="flex-1"
            name="password"
            :placeholder="$t('passwordMinimumCharacters')"
            autocomplete="new-password"
            type="password"
            color="white"
          />
        </div>
        <div class="w-full">
          <UiInput
            v-model="form.password_confirmation"
            class="flex-1"
            name="password_confirmation"
            :placeholder="$t('repeatPassword')"
            type="password"
            color="white"
          />
        </div>
      </div>
      <UiButton
        :loading="isSubmitting"
        type="submit"
        color="white"
        class="mt-4 w-full"
      >
        {{ $t("singUp") }}
      </UiButton>
      <div class="ui-text-xs text-white text-center mt-4">
        {{ $t("alreadyHaveAnAccount") }}
        <NuxtLink
          class="ui-text-xs text-white underline underline-offset-2 hover:no-underline"
          :to="localePath({ path: '/sign-in', query: route.query })"
        >
          {{ $t("signIn") }}
        </NuxtLink>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { object, ref, string } from "yup";
import { SignUpForm } from "ecom-types";
import { FetchError } from "ofetch";

// const { $api, $socialAuth } = useNuxtApp();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const localePath = useLocalePathPolyfill();

const generalStore = useGeneralStore();

const { openSystemDialog } = useSystemDialogs();
const { onSignUp } = useAuth();
const initialSignUpForm: SignUpForm = {
  first_name: "",
  last_name: "",
  password: "",
  password_confirmation: "",
  email: "",
};

const schemaSignUp = object().shape({
  email: string().email().required().label(t("email2")),
  first_name: string().required().label(t("firstName")),
  last_name: string().required().label(t("lastName")),
  password: string()
    .matches(passwordRegexp, {
      message: t("passwordRequirements"),
    })
    .required()
    .label(t("password")),
  password_confirmation: string()
    .oneOf([ref("password"), ""], "Passwords must match")
    .required()
    .label(t("repeatPassword")),
});

const form = reactive<SignUpForm>(
  JSON.parse(JSON.stringify(initialSignUpForm)),
);

const {
  handleSubmit: handleSubmitSignUp,
  resetForm: resetFormSignUp,
  setErrors: setErrorsSignUp,
  isSubmitting,
} = useForm({
  initialValues: initialSignUpForm,
  validationSchema: schemaSignUp,
});

async function successAuth() {
  if (route.query.redirect) {
    await router.replace(localePath(route.query.redirect as string));
  } else {
    await router.replace(localePath("/profile"));
  }
}

const onSubmit = handleSubmitSignUp(async (values) => {
  try {
    await onSignUp(values);
    resetFormSignUp();
    openSystemDialog({
      type: "alert",
      title: t("almostDone"),
      text: t("checkYourInboxWeHaveSentANote"),
      successBtnText: t("close"),
      successHandler: async () => {
        await router.push(localePath("/"));
      },
    });
  } catch (error) {
    if (error instanceof FetchError) {
      const errors = responseErrorToObjectErrors(error?.data?.errors, form);
      if (!isEmpty(errors)) {
        setErrorsSignUp(errors);
      } else {
        openSystemDialog({
          type: "alert",
          title: `${t("error")}!`,
          text: error?.data?.errors[0].message,
        });
      }
    }
  }
});
</script>
